/* ═══════════════════════════════════════════════════════════════
   Clerk sign-in surfaces (docs/AUTH_CLERK_PLAN.md)

   - ClerkSignInPanel  (web): Clerk <SignIn/> → /auth/clerk-exchange → SF JWT
   - DesktopSignInPanel (exe): NO auth forms — opens the system browser,
     receives the session via specforma:// deep link (paste fallback).
   - DesktopHandoff     (web): the page the exe opens (?desktop-signin=1);
     ALWAYS runs a fresh Clerk sign-in (never reuses an existing session —
     FreshVerificationGate), then hands the SpecForma JWT back through the
     deep link.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import {
  ClerkProvider,
  ClerkLoading,
  ClerkLoaded,
  SignIn,
  useAuth,
  useClerk,
} from '@clerk/clerk-react';
import { AuthService, getAuthToken } from '../services/authService';
import { Button } from '../components/ui/button';
import { Icon } from '../components/ui/Icon';
import { rem } from '../lib/rem';
import type { AuthUser } from '../services/authService';
import {
  CLERK_PUBLISHABLE_KEY,
  DESKTOP_DEEP_LINK_PREFIX,
  DESKTOP_SIGNIN_URL,
} from '../lib/clerkConfig';

interface AuthProps {
  onAuth: (user: AuthUser) => void;
}

/* Runs once a Clerk session exists: swap it for the SpecForma JWT. */
function ExchangeBridge({
  onAuth,
  onError,
  onBridging,
}: AuthProps & { onError: (msg: string) => void; onBridging?: (active: boolean) => void }) {
  const { isSignedIn, getToken } = useAuth();
  const ran = useRef(false);
  useEffect(() => {
    if (!isSignedIn || ran.current) return;
    ran.current = true;
    onBridging?.(true);
    (async () => {
      try {
        const clerkToken = await getToken();
        if (!clerkToken) throw new Error('Sign-in session missing — try again');
        const res = await AuthService.signInWithClerk(clerkToken);
        if (!res.user) throw new Error(res.error || 'Sign-in failed');
        onAuth(res.user);
      } catch (err: any) {
        ran.current = false;
        onError(err?.message || 'Sign-in failed');
      } finally {
        onBridging?.(false);
      }
    })();
  }, [isSignedIn, getToken, onAuth, onError, onBridging]);
  return null;
}

/* ONE ClerkProvider per page — clerk-js is a singleton and crashes if two
   providers mount. Web pages wrap themselves in this; inner components
   (panel, bridge) assume an ancestor provider. */
export function SpecFormaClerkProvider({ children }: { children: React.ReactNode }) {
  return <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>{children}</ClerkProvider>;
}

export function ClerkSignInPanel({
  onAuth,
  redirectUrl = '/',
}: AuthProps & { redirectUrl?: string }) {
  const [error, setError] = useState('');
  const [bridging, setBridging] = useState(false);
  const { isSignedIn } = useAuth();
  return (
    <>
      <ExchangeBridge onAuth={onAuth} onError={setError} onBridging={setBridging} />
      {error && (
        <div
          role="alert"
          style={{
            margin: `0 0 ${rem(12)}`,
            padding: `${rem(8)} var(--space-3)`,
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-base)',
            background: 'rgba(220,53,69,.06)',
            color: 'var(--dg)',
            border: '1px solid rgba(220,53,69,.15)',
          }}
        >
          {error}
        </div>
      )}
      {/* A blocked or slow Clerk script must never look like a blank card —
          say what's happening at every stage. */}
      <ClerkLoading>
        <div
          style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--tm)',
            padding: 'var(--space-4) 0',
          }}
          role="status"
        >
          Loading secure sign-in… If this never loads, an ad blocker may be blocking it — pause it
          for this site and refresh.
        </div>
      </ClerkLoading>
      <ClerkLoaded>
        {bridging && (
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--tm)',
              padding: 'var(--space-2) 0',
            }}
            role="status"
          >
            Completing sign-in…
          </div>
        )}
        {/* NEVER mount <SignIn> while a Clerk session exists: Clerk redirects
            signed-in visitors to forceRedirectUrl — on the handoff page that is
            THIS page, which reload-looped ~1/sec racing the exchange until the
            exchange happened to win (Josh, 2026-08-10). Signed in →
            ExchangeBridge completes alone; the status line above says so. */}
        {!isSignedIn && (
          /* Clerk fully reloads the page after some flows (e.g. new-device email
             verification) — forceRedirectUrl keeps us on the right page, and the
             exchange completes after the reload via the signed-in branch. */
          <SignIn
            routing="hash"
            forceRedirectUrl={redirectUrl}
            signUpForceRedirectUrl={redirectUrl}
          />
        )}
        {isSignedIn && !bridging && (
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--tm)',
              padding: 'var(--space-2) 0',
            }}
            role="status"
          >
            Completing sign-in…
          </div>
        )}
      </ClerkLoaded>
    </>
  );
}

/* Silent bridge (web only, inside SpecFormaClerkProvider): if the browser holds
   a Clerk session but no SpecForma session, exchange automatically. */
export function WebClerkAutoAuth({ onAuth }: AuthProps) {
  return (
    <ExchangeBridge onAuth={onAuth} onError={(msg) => console.warn('[clerk-auto-auth]', msg)} />
  );
}

/* Desktop: browser handoff. No credentials ever touch the exe. */
export function DesktopSignInPanel({ onAuth }: AuthProps) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pasted, setPasted] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  async function adopt(token: string) {
    if (!token) return;
    setBusy(true);
    setError('');
    const res = await AuthService.adoptToken(token);
    if (res.user) onAuth(res.user);
    else {
      setError(res.error || 'That sign-in code did not work');
      setBusy(false);
    }
  }

  useEffect(() => {
    const off = window.api?.onDeepLink?.((url: string) => {
      const idx = url.indexOf('#token=');
      if (idx >= 0) adopt(decodeURIComponent(url.slice(idx + 7)));
    });
    return typeof off === 'function' ? off : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: rem(14) }}>
      <Button
        onClick={() => {
          setWaiting(true);
          setError('');
          window.api?.openExternal?.(DESKTOP_SIGNIN_URL);
        }}
        disabled={busy}
        className="w-full"
      >
        {busy ? 'Signing in…' : waiting ? 'Reopen Sign-In Page' : 'Sign In'}
      </Button>
      {waiting && !busy && (
        <div
          style={{
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            color: 'var(--tm)',
            lineHeight: 1.5,
          }}
          role="status"
        >
          Finish signing in in your browser — you'll be brought back here automatically.
        </div>
      )}
      {waiting && !showFallback && !busy && (
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => setShowFallback(true)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              color: 'var(--tm)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Didn't come back automatically?
          </button>
        </div>
      )}
      {showFallback && (
        <>
          <div
            style={{
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              color: 'var(--tm)',
              lineHeight: 1.5,
            }}
          >
            The browser's "You're signed in" page shows a long code — copy it and paste it here.
            (Not the 6-digit email code.)
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <input
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              placeholder="Paste the long code"
              aria-label="Sign-in code from the browser"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pasted.trim()) adopt(pasted.trim());
              }}
              style={{
                flex: 1,
                padding: `${rem(8)} ${rem(10)}`,
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--bd)',
                background: 'var(--bg)',
                color: 'var(--tx)',
                fontSize: 'var(--font-size-base)',
              }}
            />
            <Button
              variant="outline"
              onClick={() => adopt(pasted.trim())}
              disabled={busy || !pasted.trim()}
            >
              Use code
            </Button>
          </div>
        </>
      )}
      {error && (
        <div
          role="alert"
          style={{
            padding: `${rem(8)} var(--space-3)`,
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-base)',
            background: 'rgba(220,53,69,.06)',
            color: 'var(--dg)',
            border: '1px solid rgba(220,53,69,.15)',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

/* Classic email/password sign-in — no third-party script involved. This is the
   guaranteed path when an ad blocker breaks the Clerk widget. */
function ClassicSignInForm({ onAuth }: AuthProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    const res = await AuthService.signIn(email.trim(), password);
    if (res.user) onAuth(res.user);
    else {
      setError(res.error || 'Sign-in failed');
      setBusy(false);
    }
  }

  const inp: React.CSSProperties = {
    width: '100%',
    padding: `${rem(9)} ${rem(11)}`,
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--bd)',
    background: 'var(--bg)',
    color: 'var(--tx)',
    fontSize: 'var(--font-size-base)',
  };

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: rem(10) }}>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        aria-label="Email"
        autoComplete="email"
        required
        style={inp}
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        aria-label="Password"
        autoComplete="current-password"
        required
        style={inp}
      />
      {error && (
        <div
          role="alert"
          style={{
            padding: `${rem(8)} var(--space-3)`,
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-base)',
            background: 'rgba(220,53,69,.06)',
            color: 'var(--dg)',
            border: '1px solid rgba(220,53,69,.15)',
          }}
        >
          {error}
        </div>
      )}
      <Button type="submit" disabled={busy || !email || !password} className="w-full">
        {busy ? 'Signing in…' : 'Sign In'}
      </Button>
    </form>
  );
}

/* Desktop sign-ins must ALWAYS verify: a Clerk session lingering in this
   browser from before is ended before the sign-in form mounts, otherwise
   ExchangeBridge would silently hand the desktop a session nobody re-verified
   (Josh, 2026-08-10). Signing out here also signs this browser out of the web
   app — that is the accepted cost of forcing re-verification.

   The per-tab flag distinguishes a STALE session from one the user JUST
   created: Microsoft SSO and new-device email verification return here via a
   full-page redirect already signed in, and nuking that session would loop
   the user forever. The exe opens a fresh tab per sign-in, so the flag never
   leaks across attempts. */
const HANDOFF_VERIFYING_FLAG = 'sf.handoffVerifying';
function FreshVerificationGate({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const clerk = useClerk();
  const [ready, setReady] = useState(false);
  const ran = useRef(false);
  useEffect(() => {
    if (!isLoaded || ran.current) return;
    ran.current = true;
    let midFlow = false;
    try {
      midFlow = sessionStorage.getItem(HANDOFF_VERIFYING_FLAG) === '1';
      sessionStorage.setItem(HANDOFF_VERIFYING_FLAG, '1');
    } catch {
      /* sessionStorage blocked — fail open to the sign-out path */
    }
    if (isSignedIn && !midFlow) {
      clerk
        .signOut()
        .catch(() => {})
        .finally(() => setReady(true));
    } else {
      setReady(true);
    }
  }, [isLoaded, isSignedIn, clerk]);
  if (!ready)
    return (
      <div
        style={{
          fontSize: 'var(--font-size-base)',
          color: 'var(--tm)',
          padding: 'var(--space-4) 0',
        }}
        role="status"
      >
        Preparing sign-in… If this never loads, an ad blocker may be blocking it — use the password
        sign-in option below.
      </div>
    );
  return <>{children}</>;
}

/* Web page the exe opens: run a FRESH sign-in, hand the JWT back. Never
   reuses an existing web session — see FreshVerificationGate. */
export function DesktopHandoff() {
  const [sfToken, setSfToken] = useState<string | null>(null);
  const [usePassword, setUsePassword] = useState(false);
  const [showTrouble, setShowTrouble] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (sfToken && !fired.current) {
      fired.current = true;
      try {
        sessionStorage.removeItem(HANDOFF_VERIFYING_FLAG);
      } catch {
        /* ignore */
      }
      window.location.href = DESKTOP_DEEP_LINK_PREFIX + encodeURIComponent(sfToken);
    }
  }, [sfToken]);

  const shell: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    color: 'var(--tx)',
    fontFamily: 'inherit',
  };
  const card: React.CSSProperties = {
    width: rem(440),
    maxWidth: '92vw',
    padding: rem(28),
    borderRadius: rem(12),
    background: 'var(--sf)',
    border: '1px solid var(--bd)',
    display: 'flex',
    flexDirection: 'column',
    gap: rem(14),
  };
  const logo = (
    <div
      style={{
        textAlign: 'center',
        fontSize: 'var(--font-size-2xl)',
        fontWeight: 700,
        letterSpacing: '-0.02em',
      }}
    >
      SpecForma
    </div>
  );

  if (!sfToken)
    return (
      <div style={shell}>
        <div style={card}>
          {logo}
          <div
            style={{ textAlign: 'center', fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}
          >
            Sign in to continue to the desktop app
          </div>
          {usePassword ? (
            <>
              <ClassicSignInForm onAuth={() => setSfToken(getAuthToken())} />
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => setUsePassword(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    color: 'var(--tm)',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Back to standard sign-in
                </button>
              </div>
            </>
          ) : (
            <>
              <SpecFormaClerkProvider>
                <FreshVerificationGate>
                  <ClerkSignInPanel
                    onAuth={() => setSfToken(getAuthToken())}
                    redirectUrl="/?desktop-signin=1"
                  />
                </FreshVerificationGate>
              </SpecFormaClerkProvider>
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => setUsePassword(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    color: 'var(--tm)',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Trouble loading? Sign in with your password instead
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );

  return (
    <div style={shell}>
      <div style={card}>
        {logo}
        <div
          style={{
            textAlign: 'center',
            fontSize: 'calc(var(--font-size-base) * 34 / 13)',
            lineHeight: 1,
            color: 'var(--ok)',
          }}
        >
          ✓
        </div>
        <div style={{ textAlign: 'center', fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
          You're signed in
        </div>
        <div
          style={{
            textAlign: 'center',
            fontSize: 'var(--font-size-base)',
            color: 'var(--tm)',
            lineHeight: 1.5,
          }}
        >
          Opening SpecForma… once the app appears, you can close this tab.
        </div>
        <Button
          onClick={() => {
            window.location.href = DESKTOP_DEEP_LINK_PREFIX + encodeURIComponent(sfToken);
          }}
          className="w-full"
        >
          Open SpecForma
        </Button>
        {!showTrouble ? (
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => setShowTrouble(true)}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tm)',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              App didn't open?
            </button>
          </div>
        ) : (
          <>
            <div
              style={{
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tm)',
                lineHeight: 1.5,
              }}
            >
              In the app, click "Didn't come back automatically?" and paste this code:
            </div>
            <textarea
              readOnly
              value={sfToken}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Sign-in code for the desktop app"
              style={{
                width: '100%',
                height: 'var(--space-8)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'monospace',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--bd)',
                background: 'var(--bg)',
                color: 'var(--tx)',
                resize: 'none',
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}
